import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

import PublishToggle from './PublishToggle';
import CoverImageInput from './CoverImageInput';
import CategorySelector from './CategorySelector';
import CategoryCreator from './CategoryCreator';
import BlogEditor from './BlogEditor';

const BlogForm = ({ initialData = null }) => {
  const [categories, setCategories] = useState([]);
  const methods = useForm({
    defaultValues: {
      title: '',
      content_html: '',
      cover_image: '',
      is_published: false,
      category_ids: [],
    },
  });

  const { register, handleSubmit, control, reset, setValue, watch } = methods;
  const navigate = useNavigate();
  const isEditing = !!initialData;

  // Obtener categorías desde backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/api/blog-categories');
        setCategories(res.data);
      } catch (err) {
        console.error('Error al cargar categorías:', err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Inicializar valores del formulario si se edita
  useEffect(() => {
    if (isEditing) {
      reset({
        title: initialData.title || '',
        content_html: initialData.content_html || '',
        cover_image: initialData.cover_image || '',
        is_published: initialData.is_published === 1 || initialData.is_published === true,
        category_ids: initialData.category_ids || []
      });
    } else {
      reset();
    }
  }, [initialData, reset, isEditing]); // <-- actualizado para evitar warning

  // Agregar nueva categoría al estado y marcarla como seleccionada
  const handleNewCategoryCreated = (newCat) => {
    setCategories((prev) => [...prev, newCat]);
    setValue('category_ids', [...(watch('category_ids') || []), newCat.id]);
  };

  // Enviar datos
  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await api.put(`/api/blog/${initialData.id}`, data);
      } else {
        await api.post('/api/blog', data);
      }
      navigate('/blog');
    } catch (err) {
      console.error('Error al guardar post:', err);
      alert('Error al guardar publicación');
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-7xl mx-auto px-6 py-8 flex flex-col lg:flex-row gap-6 font-[Manrope]"
      >
        {/* Columna principal */}
        <div className="flex-1 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h1 className="text-xl font-semibold mb-4">
              {isEditing ? 'Editar publicación' : 'Nueva publicación'}
            </h1>
            <input
              type="text"
              placeholder="Título de la publicación"
              {...register('title', { required: true })}
              className="w-full px-4 py-3 text-lg border rounded-md mb-4"
            />
            <BlogEditor control={control} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-[300px] space-y-6 shrink-0">
          {/* Publicar */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h2 className="text-sm font-semibold mb-3">Publicar</h2>
            <PublishToggle />
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={() => navigate('/blog')}
                className="w-full px-3 py-2 text-sm border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="w-full px-3 py-2 text-sm rounded-md bg-black text-white hover:bg-indigo-700"
              >
                Guardar
              </button>
            </div>
          </div>

          {/* Imagen de portada */}
          <CoverImageInput />

          {/* Categorías */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h2 className="text-sm font-semibold mb-3">Categorías</h2>
            <CategoryCreator onCreated={handleNewCategoryCreated} categories={categories} />
            <CategorySelector categories={categories} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default BlogForm;
